import React, { useState, useEffect, forwardRef, useCallback } from "react";
import { InputMask } from "@react-input/mask";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Flag from "react-world-flags";
import InputAdornment from "@mui/material/InputAdornment";

const normalizeDigits = (val: string) => val.replace(/\D/g, "");

type InputProps = TextFieldProps & {
  defaultCode: string;
  loading?: boolean;
};

const PhoneNumberInput = forwardRef<HTMLDivElement, InputProps>(
  (
    {
      loading = false,
      disabled = false,
      InputProps,
      defaultCode,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    // Track if we're in domestic mode (UK only, when user types 0)
    const [isDomesticMode, setIsDomesticMode] = useState(() => {
      const initialDigits = normalizeDigits((value as string) || "");
      return defaultCode === "UK" && initialDigits.startsWith("0");
    });

    const getMask = (code: string, isDomestic: boolean = false) => {
      switch (code) {
        case "US":
          return "___ ___ ____";
        case "UK":
          // UK domestic format: 0NNN NNNN NNNN (11 digits, e.g., 020 7946 0958)
          // UK international format: NNNN NNNNNN (10 digits, e.g., 20 7946 0958)
          return isDomestic ? "_ ___ ____ ____" : "____ ______";
        default:
          return "";
      }
    };

    const getFlagCode = (code: string) => {
      switch (code) {
        case "US":
          return "US";
        case "UK":
          return "GB";
        default:
          return "";
      }
    };

    const flagCode = getFlagCode(defaultCode);

    // Format phone number for display
    const applyFormat = (
      val: string,
      code: string,
      isDomestic: boolean = false,
    ) => {
      let formatted = normalizeDigits(val);

      switch (code) {
        case "US":
          // US: Store as 10 digits, display as NNN NNN-NNNN
          if (formatted.length > 6) {
            formatted = formatted.replace(/(\d{3})(\d{3})(\d+)/, "$1 $2-$3");
          } else if (formatted.length > 3) {
            formatted = formatted.replace(/(\d{3})(\d+)/, "$1 $2");
          }
          break;
        case "UK":
          if (isDomestic) {
            // UK Domestic: Display as 0NNN NNNN NNNN (e.g., 020 7946 0958)
            if (formatted.length > 7) {
              // Format: 0NNN NNNN NNNN
              formatted = formatted.replace(
                /(\d)(\d{3})(\d{4})(\d+)/,
                "$1$2 $3 $4",
              );
            } else if (formatted.length > 3) {
              // Partial format: 0NNN NNNN
              formatted = formatted.replace(/(\d)(\d{3})(\d+)/, "$1$2 $3");
            }
          } else {
            // UK International: Display as NNNN NNNNNN (10 digits, e.g., 4444 666666)
            if (formatted.length > 4) {
              formatted = formatted.replace(/(\d{4})(\d+)/, "$1 $2");
            }
          }
          break;
        default:
          break;
      }

      return formatted;
    };

    const [formattedValue, setFormattedValue] = useState(() => {
      const raw = (value as string) || "";
      return applyFormat(raw, defaultCode, isDomesticMode);
    });

    // Get country code prefix for display
    const getCountryCodePrefix = (
      code: string,
      isDomestic: boolean,
    ): string => {
      if (code === "UK" && isDomestic) {
        return "0"; // Domestic format shows 0 instead of +44
      }
      switch (code) {
        case "US":
          return "+1";
        case "UK":
          return "+44";
        default:
          return "";
      }
    };

    // Sync with prop value, but keep formatting stable for display.
    useEffect(() => {
      const nextRaw = (value as string) || "";
      const nextDigits = normalizeDigits(nextRaw);
      const nextIsDomestic =
        defaultCode === "UK" && nextDigits.startsWith("0") && nextDigits !== "";

      if (defaultCode !== "UK" && isDomesticMode) setIsDomesticMode(false);
      if (defaultCode === "UK" && nextIsDomestic !== isDomesticMode)
        setIsDomesticMode(nextIsDomestic);

      const nextFormatted = applyFormat(nextRaw, defaultCode, nextIsDomestic);
      setFormattedValue(nextFormatted);
    }, [value, defaultCode]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const digits = normalizeDigits(inputValue);

        // For UK: Detect if user is typing in domestic format (starts with 0)
        if (defaultCode === "UK") setIsDomesticMode(digits.startsWith("0"));
        if (defaultCode !== "UK" && isDomesticMode) setIsDomesticMode(false);

        // Update local state immediately for responsive UI
        setFormattedValue(inputValue);

        if (onChange) {
          let storedDigits = digits;
          if (defaultCode === "UK" && storedDigits.startsWith("0")) {
            storedDigits = storedDigits.substring(1);
          }
          onChange({
            target: { value: storedDigits },
          } as React.ChangeEvent<HTMLInputElement>);
        }
      },
      [defaultCode, isDomesticMode, onChange],
    );

    return (
      <TextField
        ref={ref}
        fullWidth
        autoComplete="off"
        value={formattedValue}
        onChange={handleInputChange}
        disabled={disabled || loading}
        InputProps={{
          ...InputProps,
          startAdornment: (
            <InputAdornment position="start" className="pr-3">
              <Flag code={flagCode} style={{ width: 24, height: 16 }} />
              <span className="pl-2">
                {getCountryCodePrefix(defaultCode, isDomesticMode)}
              </span>
            </InputAdornment>
          ),
          readOnly: InputProps?.readOnly || loading,
          inputComponent: InputMask,
          inputProps: {
            mask: getMask(defaultCode, isDomesticMode),
            replacement: { _: /\d/ },
          },
        }}
        {...props}
      />
    );
  },
);

PhoneNumberInput.displayName = "PhoneNumberInput";

export default PhoneNumberInput;
